import { decodeFunctionResult, encodeFunctionData, getAddress, type Address, type Hex } from "viem";
import { encodeRedeemWithFeesCall, ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { ContractReadError, contractReadError } from "../contracts/contract-errors";
import { readNftState } from "./nft-lifecycle";
import { currentChainId, type Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { readRedeemFeeQuote, type RedeemFeeRoute } from "./redeem-fee-quote";

export async function checkRedeemState(provider: Eip1193Provider, input: {
  account: string; chainId: number; collection: Address; tokenId: bigint; sourceToken: string; minimumPayoutWei: bigint;
  feeRoute?: RedeemFeeRoute | null; contextRevision?: string;
}, assertCurrent: () => void, options: RpcReadOptions = {}) {
  if (input.feeRoute) {
    const route = input.feeRoute;
    if (route.chainId !== input.chainId || route.collection.toLowerCase() !== input.collection.toLowerCase() ||
      route.sourceToken.toLowerCase() !== input.sourceToken.toLowerCase()) throw new Error("Redemption route context mismatch");
    const result = await readRedeemFeeQuote(provider, { account: getAddress(input.account), tokenId: input.tokenId,
      contextRevision: input.contextRevision ?? "", route }, assertCurrent, options);
    if (!result.available) throw new Error(result.message);
    if (result.quote.payoutWei < input.minimumPayoutWei) throw new Error("PayoutBelowMinimum: Prepare and review a new quote; the minimum was not lowered.");
    return result.quote;
  }
  const state = await readNftState(provider, input, assertCurrent, options);
  if (state.status !== 3) throw new Error("InvalidRewardStatus: Only a finalized NFT can redeem.");
  if (state.sourceToken.toLowerCase() !== input.sourceToken.toLowerCase()) throw new Error("NFT source token changed. Review again.");
  const read = async (functionName: "ownerOf" | "previewRedeem") => {
    assertCurrent();
    let raw: unknown;
    try { raw = await readRpc(provider, { method: "eth_call", params: [{ to: input.collection,
      data: encodeFunctionData({ abi: ponsRewardNftAbi, functionName, args: [input.tokenId] }) }, "latest"] }, options); }
    catch (error) { options.signal?.throwIfAborted(); assertCurrent(); throw contractReadError(error); }
    assertCurrent();
    if (typeof raw !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(raw)) throw new Error("Invalid redemption state response");
    return raw as Hex;
  };
  const owner = decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "ownerOf", data: await read("ownerOf") });
  if (owner.toLowerCase() !== input.account.toLowerCase()) throw new Error("UnauthorizedRedeemer: Connect the current NFT owner wallet.");
  const [source, , , payout, redeemable] = decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "previewRedeem", data: await read("previewRedeem") });
  if (source.toLowerCase() !== input.sourceToken.toLowerCase()) throw new Error("Redemption preview source mismatch");
  if (payout === 0n) throw new Error("PayoutIsZero: The source-token pool cannot pay this NFT now.");
  if (!redeemable) throw new Error("NFT is not redeemable at the current chain state.");
  if (payout < input.minimumPayoutWei) throw new Error("PayoutBelowMinimum: The pool changed beyond your reviewed minimum. Prepare and review again.");
  if (await currentChainId(provider, options) !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  assertCurrent();
}

/** Validate the exact protected fee call before publishing its review. */
export async function simulateRedeemWithFeesReview(provider: Eip1193Provider, input: {
  account: string; chainId: number; collection: string; tokenId: bigint; minimumPayoutWei: bigint; deadline: bigint;
  blockNumber?: bigint;
}, assertCurrent: () => void, options: RpcReadOptions = {}) {
  assertCurrent();
  if (await currentChainId(provider, options) !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  assertCurrent();
  const data = encodeRedeemWithFeesCall({ tokenId: input.tokenId, minPayoutWei: input.minimumPayoutWei, deadline: input.deadline });
  let result: unknown;
  try {
    result = await readRpc(provider, { method: "eth_call", params: [{ from: input.account, to: input.collection,
      data, value: "0x0" }, input.blockNumber === undefined ? "latest" : `0x${input.blockNumber.toString(16)}`] }, options);
  } catch (error) {
    options.signal?.throwIfAborted();
    assertCurrent();
    throw contractReadError(error, input.collection);
  }
  assertCurrent();
  if (result !== "0x") throw new Error("Invalid redemption simulation response");
  if (await currentChainId(provider, options) !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
  assertCurrent();
}

/** A forced minimum revert reveals the contract's actual post-sweep payout at the quote block. */
export async function readSimulatedRedeemWithFeesPayout(provider: Eip1193Provider, input: {
  account: string; chainId: number; collection: string; tokenId: bigint; deadline: bigint; blockNumber: bigint;
}, assertCurrent: () => void, options: RpcReadOptions = {}): Promise<bigint> {
  const maximum = (1n << 256n) - 1n;
  try {
    await simulateRedeemWithFeesReview(provider, { ...input, minimumPayoutWei: maximum }, assertCurrent, options);
  } catch (error) {
    options.signal?.throwIfAborted();
    assertCurrent();
    if (error instanceof ContractReadError && error.errorName === "PayoutBelowMinimum" &&
      error.minimumPayoutWei === maximum && error.payoutWei && error.payoutWei > 0n && error.payoutWei < maximum) {
      if (await currentChainId(provider, options) !== input.chainId) throw new Error(`WRONG_CHAIN:${input.chainId}`);
      assertCurrent();
      return error.payoutWei;
    }
    throw error;
  }
  throw new Error("Redemption simulation did not return a verifiable payout.");
}
